/**
 * Doomsworn — ROLES (instead of classes)
 *
 * A Role is your function in the Indenture, not your job. Each grants a small
 * package of abilities and a **Pull** — a way the world tries to cast you.
 * Playing against your Pull is supported and interesting, and the GM should
 * lean on it rather than enforce it.
 *
 * Roles are surfaced through the generic `ClassDefinition` shape so the
 * existing character pipeline keeps working. There are no subclasses, no
 * multiclassing, and no 20-level class tables — a Role is a starting position
 * and a set of pressures, not a progression chart.
 */
import { ATTUNEMENT } from './abilities.js';
export const DOOMSWORN_ROLES = [
    {
        id: 'heir',
        name: 'The Heir',
        hitDie: 6,
        abilityPackage: { [ATTUNEMENT]: 2, cha: 1 },
        primaryAbility: [ATTUNEMENT, 'cha'],
        savingThrows: ['cha', ATTUNEMENT],
        startsWith: 'A House pact and inherited Debt',
        pull: 'Your House expects you to spend yourself',
        pillars: ['pact'],
        startingDebt: 5,
        grants: [
            'A named Power your House is sworn to, and standing to Draw on it',
            'Inherited Debt of 5 to that Power — you did not incur it and you owe it anyway',
            'A House name that opens doors and closes others',
        ],
    },
    {
        id: 'blade',
        name: 'The Blade',
        hitDie: 10,
        abilityPackage: { str: 1, dex: 1, con: 1 },
        primaryAbility: ['str', 'dex'],
        savingThrows: ['str', 'con'],
        startsWith: 'Sworn to a person or a Power',
        pull: 'You are pointed, not consulted',
        pillars: ['pact'],
        grants: [
            'An oath to a specific person or Power, written down and known to others',
            'Martial training: the best weapons and armour in the party',
            'Someone else decides where you are aimed, and everyone assumes you agree',
        ],
    },
    {
        id: 'unsworn',
        name: 'The Unsworn',
        hitDie: 8,
        abilityPackage: { [ATTUNEMENT]: 2, wis: 1 },
        primaryAbility: [ATTUNEMENT, 'wis'],
        savingThrows: ['con', ATTUNEMENT],
        startsWith: 'A rune, and the cult that came with it',
        pull: 'The Old Names want you deeper',
        pillars: ['rune'],
        grants: [
            'One rune at rating 1, paid for in Attunement already sacrificed',
            'A cult that considers you theirs, and a rival cult that considers you a problem',
            'Your rating is public. Anyone who looks can read it.',
        ],
    },
    {
        id: 'clerk',
        name: 'The Clerk',
        hitDie: 6,
        abilityPackage: { int: 2, wis: 1 },
        primaryAbility: ['int', 'wis'],
        savingThrows: ['int', 'wis'],
        startsWith: 'Deadhouse training; reads instruments',
        pull: 'You are asked to know things and say nothing',
        pillars: [],
        grants: [
            'You can read a pact instrument and say what it actually commits',
            'Deadhouse standing: charter neutrality that both Powers are obliged to respect',
            'Access to filings nobody else in the party can request',
        ],
    },
    {
        id: 'foreigner',
        name: 'The Foreigner',
        hitDie: 6,
        abilityPackage: { int: 2, dex: 1 },
        primaryAbility: ['int'],
        savingThrows: ['int', 'dex'],
        startsWith: 'Cast magic and a revocable license',
        pull: 'Everyone assumes you are leaving',
        pillars: ['cast'],
        grants: [
            'Cast spells: prepared slots, Open5e spells filtered to levels 1–5',
            'A residence license that is renewed annually and could stop being renewed on any morning',
            'Nothing owns you, and nothing here is yours',
        ],
    },
    {
        id: 'ruined',
        name: 'The Ruined',
        hitDie: 8,
        abilityPackage: { con: 2, [ATTUNEMENT]: 1 },
        primaryAbility: ['con', ATTUNEMENT],
        savingThrows: ['con', ATTUNEMENT],
        startsWith: 'Debt to a Power that stopped answering',
        pull: 'The Sump wants you back',
        pillars: ['pact'],
        startingDebt: 10,
        grants: [
            'Debt of 10 to a Power that has not answered a Draw in a generation',
            'At least one Lien still in force, being enforced by nobody',
            'A Hungerbound relic, or a plausible route to one — the on-ramp for those with no House',
        ],
    },
    {
        id: 'favoured',
        name: 'The Favoured',
        hitDie: 6,
        abilityPackage: { [ATTUNEMENT]: 3 },
        primaryAbility: [ATTUNEMENT],
        savingThrows: ['cha', ATTUNEMENT],
        startsWith: 'Gifts you did not request',
        pull: 'Something is grooming you',
        pillars: ['pact'],
        grants: [
            'Two gifts from a Power you never bargained with, and no record of a pact',
            'No Debt on the ledger yet — which is the part that should worry you',
            'The gifts arrive when they arrive, not when you ask',
        ],
    },
];
export function getRole(id) {
    return DOOMSWORN_ROLES.find((r) => r.id === id.toLowerCase());
}
export const DEFAULT_ROLE_ID = 'blade';
/**
 * Roles as `ClassDefinition[]`.
 *
 * `spellcasting` is deliberately `'none'` for every Role except The Foreigner.
 * Pacts and Runes have no slots at all, and tagging them `'pact'` would invite
 * 5e slot machinery to compute a table that does not exist in this system.
 * The Foreigner is `'half'` because Cast tops out at 5th-level spells.
 */
export const DOOMSWORN_ROLE_CLASSES = DOOMSWORN_ROLES.map((role) => ({
    id: role.id,
    name: role.name,
    hitDie: role.hitDie,
    primaryAbility: [...role.primaryAbility],
    savingThrows: [...role.savingThrows],
    spellcasting: role.pillars.includes('cast') ? 'half' : 'none',
    description: `${role.startsWith}. Pull: ${role.pull}.${role.pillars.length ? ` Pillar: ${role.pillars.join(', ')}.` : ' No pillar at creation.'}`,
}));
//# sourceMappingURL=roles.js.map